<template>
  <div class="content">
    <v-card class="d-flex pa-1 bg" outlined>
      <div class="mr-auto d-flex top-button-group">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="expandAll()"><v-icon small>mdi-arrow-expand-vertical</v-icon></v-btn>
          </template>
          <span>{{ $t('common.fold') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="collapseAll()"><v-icon small>mdi-arrow-collapse-vertical</v-icon></v-btn>
          </template>
          <span>{{ $t('common.unfold') }}</span>
        </v-tooltip>
      </div>
      <div class="top-button-group">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="edit()" :disabled="disabled.edit"><v-icon small>mdi-file-document-edit-outline</v-icon></v-btn>
          </template>
          <span>{{ $t('common.edit') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="add()" :disabled="disabled.add"><v-icon small>mdi-plus-box-outline</v-icon></v-btn>
          </template>
          <span>{{ $t('common.add') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="remove()" :disabled="disabled.remove"><v-icon small>mdi-minus-box-outline</v-icon></v-btn>
          </template>
          <span>{{ $t('common.delete') }}</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" x-small right class="mb-2" @click="save()"><v-icon small>mdi-content-save</v-icon></v-btn>
          </template>
          <span>{{ $t('common.save') }}</span>
        </v-tooltip>
      </div>
    </v-card>
    <v-row>
      <v-col cols="8">
        <v-card outlined min-height="350">
          <vue-scrollbar class="preference-scrollbar">
            <tree ref="group" :data="groups.data" :options="groups.options" @node:selected="onGroupSelected" @node:editing:start="onEditingStart" @node:editing:stop="onEditingStop" />
          </vue-scrollbar>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card outlined min-height="350">
          <tree ref="system" :data="systems.data" :options="systems.options" />
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
// https://amsik.github.io/liquor-tree/
import TreeRoot from '@/components/tree/TreeRoot.vue';
import { api } from '@/api/common/instances';
import { set } from 'lodash';

const events = [
  { name: 'tree:mounted', args: ['Tree Component'] },
  { name: 'tree:filtered', args: ['Matches', 'Filter String'] },
  { name: 'tree:data:fetch', args: ['Parent Node'] },
  { name: 'tree:data:received', args: ['Parent Node'] },
  { name: 'node:clicked', args: ['Node'] },
  { name: 'node:disabled', args: ['Node'] },
  { name: 'node:enabled', args: ['Node'] },
  { name: 'node:shown', args: ['Node'] },
  { name: 'node:hidden', args: ['Node'] },
  { name: 'node:dblclick', args: ['Node'] },
  { name: 'node:selected', args: ['Node'] },
  { name: 'node:unselected', args: ['Node'] },
  { name: 'node:checked', args: ['Node'] },
  { name: 'node:unchecked', args: ['Node'] },
  { name: 'node:expanded', args: ['Node'] },
  { name: 'node:collapsed', args: ['Node'] },
  { name: 'node:added', args: ['Node', 'New Node'] },
  { name: 'node:removed', args: ['Node'] },
  { name: 'node:text:changed', args: ['Node', 'New Text', 'Old Text'] },
  { name: 'node:editing:start', args: ['Node'] },
  { name: 'node:editing:stop', args: ['Node', 'isTextChanged'] },
];
let key = 0;

export default {
  components: {
    tree: TreeRoot,
  },
  data() {
    return {
      events: [],
      selectedNode: null,
      groups: {
        data: this.getGroups(),
        options: {
          nodeIndent: 0,
          propertyNames: {
            text: 'name',
          },
          dnd: true,
        },
      },
      systems: {
        data: this.getSystems(),
        options: {
          nodeIndent: 0,
          dnd: true,
          dropTarget: {
            caller: this,
            name: 'group',
            dragFinish: this.onSystemDragFinish,
          },
        },
      },
      disabled: {
        edit: true,
        add: true,
        remove: true,
      },
    };
  },
  computed: {},
  watch: {
    selectedNode(v) {
      const systemId = v ? v.data.systemId : null;
      this.disabled.edit = !!systemId || !v;
      this.disabled.add = !v;
      this.disabled.remove = !v;
    },
  },
  methods: {
    async getGroups() {
      const data = (await api.get('/preference/groups')).data || [];
      const metaData = items => {
        items.map(item => {
          item.data = {};
          Object.keys(item)
            .filter(key => key != 'children' && key != 'data')
            .forEach(key => (item.data[key] = item[key]));
          item.state = { expanded: true };
          if (item.children.length) metaData(item.children);
        });
      };
      metaData(data);
      return data;
    },
    async getSystems() {
      const data = (await api.get('/preference/systems')).data || [];
      const systems = data.map(item => ({ text: item.id }));
      return systems;
    },
    expandAll() {
      this.getAllNodes().expand();
    },
    collapseAll() {
      this.getAllNodes().collapse();
    },
    edit() {
      this.selectedNode.startEditing();
    },
    add() {
      const node = this.$refs.group.after(this.selectedNode, ' ');
      node.select();
      node.startEditing();
    },
    async remove() {
      if (await this.$dialog.confirm(this.$t('common.delete.q'))) {
        this.selectedNode.remove();
        this.selectedNode = null;
      }
    },
    async save() {
      if (await this.$dialog.confirm(this.$t('common.save.all.q'))) {
        api.post('preference/groups', this.getTree());
      }
    },
    onGroupSelected(node) {
      this.selectedNode = node;
    },
    onEditingStart(node) {
      node.data.text = node.data.text == ' ' ? '' : node.data.text;
    },
    onEditingStop(node) {
      if (node.data.text == '') {
        node.remove();
        this.selectedNode = null;
      }
    },
    onSystemDragFinish(dropable, draggable) {
      if (dropable.data.systemId) return; // target node 가 system 일 경우 취소
      if (dropable.children.find(c => c.data.systemId == draggable.data.text)) return; // target node 하위에 같은 system 이 등록된 경우 취소
      dropable.append({
        text: draggable.data.text,
        data: {
          systemId: draggable.data.text,
        },
      });
      dropable.expand();
    },
    findPath(node, roots) {
      const paths = [];
      let item = node;
      while (item.parent != null) {
        paths.unshift(item.parent.children.findIndex(i => i.id == item.id));
        item = item.parent;
      }
      paths.unshift(roots.findIndex(i => i.id == item.id));
      return paths.join('.children.');
    },
    atom(node) {
      const atom = {};
      atom.name = node.data.text;
      if (node.data.systemId) atom.systemId = node.data.systemId;
      return Object.assign({}, atom);
    },
    getAllNodes() {
      return this.$refs.group.find({}, true);
    },
    getTree() {
      const tree = [];
      const allNodes = this.getAllNodes();
      const roots = allNodes.filter(n => n.parent == null);
      allNodes.forEach(node => set(tree, this.findPath(node, roots), this.atom(node)));
      return tree;
    },
    initEventViewer(event) {
      const events = this.events;
      return function(node, newNode) {
        let nodeText = '-';
        let targetNode = newNode && newNode.text ? newNode : node;
        if (targetNode && targetNode.text) {
          nodeText = targetNode.text;
        }
        events.push(Object.assign({}, event, { time: new Date(), nodeText, id: key++ }));
      };
    },
  },
  mounted() {
    events.forEach(event => this.$refs.group.$on(event.name, this.initEventViewer(event)));
  },
};
</script>

<style scoped>
.content {
  min-width: 800px;
  height: 300px;
}
.preference-scrollbar {
  min-height: 350px;
}
</style>
<style></style>
